import { useState, useEffect, useContext } from "react";
import { useFormik } from "formik";
import { putWithResponse } from "../../../api/apis";
import { obtenerPosicionesLaboralesApi } from "../../../api/contratacion/datosLaboralesApi";
import { mensajeEstatico, mensajeConfirmacionModal, modalGuardandoCambiosModal, mensajeExitoModal } from "../../../components/ui/sweetAlerts";
import { DataContextContractual } from "../../../contextData/contextData";

const cleanStr = (str) =>
  String(str || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[.,;:_\-/]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

const normCode = (s) => String(s || "").trim().replace(/^0+/, "");

const findPosicionInList = (list, targetCodeNorm, targetDescClean) => {
  if (!Array.isArray(list)) return null;

  const foundByCode = list.find(
    (p) => normCode(p.MOES_COD_POSICION) === targetCodeNorm
  );
  if (foundByCode) return foundByCode;

  if (targetDescClean) {
    return list.find((p) => {
      const c = cleanStr(p.MOES_DESCRIPCION_POSICION);
      return Boolean(c && (c === targetDescClean || c.includes(targetDescClean) || targetDescClean.includes(c)));
    }) || null;
  }

  return null;
};

const findPosicionLaboral = (posicionesLaborales, currentVal, nombreNuevoCargo) => {
  const targetCodeNorm = normCode(currentVal);
  const targetDescClean = cleanStr(nombreNuevoCargo);

  for (const grupo of Object.keys(posicionesLaborales)) {
    const found = findPosicionInList(posicionesLaborales[grupo], targetCodeNorm, targetDescClean);
    if (found) return found;
  }
  return null;
};

const validateCargoYUnidad = (tipo, values, errObj) => {
  if ([1, 3].includes(tipo) && !values.cod_sap_nuevo_cargo) {
    errObj.cod_sap_nuevo_cargo = "Requerido";
  }
  if ([1, 2, 3].includes(tipo)) {
    if (!values.nueva_unidad_organizativa) errObj.nueva_unidad_organizativa = "Requerido";
    if (!values.nuevo_plan_horario) errObj.nuevo_plan_horario = "Requerido";
  }
};

const validateTipoContrato = (tipo, values, errObj) => {
  if (tipo !== 4) return;
  if (!values.tipo_cambio_contrato) {
    errObj.tipo_cambio_contrato = "Requerido";
  }
  if (["02", 2].includes(values.tipo_cambio_contrato) && !values.fecha_renovacion_contrato) {
    errObj.fecha_renovacion_contrato = "Requerido";
  }
};

const validateDesvinculacion = (tipo, values, errObj) => {
  if (tipo !== 5) return;
  if (!values.id_clausula_despido) errObj.id_clausula_despido = "Requerido";
  if (!values.motivo_desvinculacion) errObj.motivo_desvinculacion = "Requerido";
};

const validateSolicitudForm = (tipo, values) => {
  const errObj = {};
  if (!values.fecha_inicio_cambio) {
    errObj.fecha_inicio_cambio = "Requerido";
  }

  validateCargoYUnidad(tipo, values, errObj);
  validateTipoContrato(tipo, values, errObj);
  validateDesvinculacion(tipo, values, errObj);

  return errObj;
};

export const useModalEditarSolicitud = (solicitud, hideModal, onSuccess) => {
  const [posicionesLaborales, setPosicionesLaborales] = useState([]);
  const [cargando, setCargando] = useState(() => Boolean(solicitud && [1, 3].includes(Number(solicitud?.id_tipo_solicitud))));
  const { dataListado, setDataListado } = useContext(DataContextContractual);

  const getPosiciones = async () => {
    setCargando(true);
    try {
      const adminSapId = [3].includes(Number(solicitud?.id_tipo_solicitud))
        ? (solicitud?.cod_sap_administrador_destino || solicitud?.cod_sap_admin_destino || solicitud?.cod_sap_administrador || null)
        : (solicitud?.cod_sap_administrador || null);
      const resp = await obtenerPosicionesLaboralesApi(adminSapId);
      if (resp?.status === "success") {
        setPosicionesLaborales(resp.data);
      }
    } catch (err) {
      console.error("Error cargando posiciones:", err);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    if (solicitud && [1, 3].includes(Number(solicitud?.id_tipo_solicitud))) {
      getPosiciones();
    } else {
      setCargando(false);
    }
  }, [solicitud]);

  useEffect(() => {
    const hasPosiciones = posicionesLaborales && Object.keys(posicionesLaborales).length > 0;
    if (!hasPosiciones || !solicitud?.cod_sap_nuevo_cargo) return;

    const currentVal = formik.values.cod_sap_nuevo_cargo || solicitud.cod_sap_nuevo_cargo;
    if (typeof currentVal !== "string" || currentVal.startsWith("{")) return;

    const found = findPosicionLaboral(posicionesLaborales, currentVal, solicitud.nombre_nuevo_cargo);

    if (found) {
      formik.setFieldValue("cod_sap_nuevo_cargo", JSON.stringify(found));
    } else {
      formik.setFieldValue("cod_sap_nuevo_cargo", "");
      formik.setFieldValue("nueva_unidad_organizativa", "");
      formik.setFieldValue("descripcion_unidad_temporis", "");
      formik.setFieldValue("nuevo_plan_horario", "");
    }
  }, [posicionesLaborales, solicitud]);

  const validate = (values) => validateSolicitudForm(Number(solicitud?.id_tipo_solicitud), values);

const buildCargoPayload = (idTipo, values, payload) => {
  if (![1, 3].includes(idTipo) || !values.cod_sap_nuevo_cargo) return;
  try {
    const parsedCargo = typeof values.cod_sap_nuevo_cargo === "string" && values.cod_sap_nuevo_cargo.startsWith("{")
      ? JSON.parse(values.cod_sap_nuevo_cargo)
      : { MOES_COD_POSICION: values.cod_sap_nuevo_cargo, MOES_DESCRIPCION_POSICION: values.nombre_nuevo_cargo };

    payload.cod_sap_nuevo_cargo = parsedCargo.MOES_COD_POSICION;
    payload.nombre_nuevo_cargo = parsedCargo.MOES_DESCRIPCION_POSICION;
    if (parsedCargo.MOES_COD_DIVISION) payload.moes_cod_division = parsedCargo.MOES_COD_DIVISION;
    if (parsedCargo.MOES_COD_SUB_DIVISION) payload.moes_cod_sub_division = parsedCargo.MOES_COD_SUB_DIVISION;
    if (parsedCargo.MOES_ID_GRUPO_DE_PERSONAL) payload.moes_id_grupo_de_personal = parsedCargo.MOES_ID_GRUPO_DE_PERSONAL;
    if (parsedCargo.MOES_ID_AREA_DE_PERSONAL) payload.moes_id_area_de_personal = parsedCargo.MOES_ID_AREA_DE_PERSONAL;
    if (parsedCargo.MOES_COD_FUNCION) payload.moes_cod_funcion = parsedCargo.MOES_COD_FUNCION;
    if (parsedCargo.MOES_COD_UNIDAD_ORGANIZATIVA) payload.moes_cod_unidad_organizativa = parsedCargo.MOES_COD_UNIDAD_ORGANIZATIVA;
    if (parsedCargo.MOES_ID_AREA_DE_NOMINA) payload.moes_id_area_de_nomina = parsedCargo.MOES_ID_AREA_DE_NOMINA;
  } catch (e) {
    console.warn("Error parseando JSON de cod_sap_nuevo_cargo al submitir:", e);
    payload.cod_sap_nuevo_cargo = values.cod_sap_nuevo_cargo;
  }
};

const buildUnidadPayload = (idTipo, values, payload) => {
  if (![1, 2, 3].includes(idTipo) || !values.nueva_unidad_organizativa) return;

  if (typeof values.nueva_unidad_organizativa === "object") {
    const uo = values.nueva_unidad_organizativa;
    payload.nueva_unidad_organizativa = uo.IdUnidadorganizativa || "";
    payload.localcodigoerp = uo.Localcodigoerp || "";
    payload.idunidadorganizativa = uo.IdUnidadorganizativa || "";
    payload.idcargogenerico = uo.IdCargogenerico || "";
    payload.idplanhorario = uo.IdPlanhorario || "";
    payload.descripcion_unidad_temporis = values.descripcion_unidad_temporis || `${uo.Unidadorganizativa} - ${uo.Cargogenericonombre}`;
  } else {
    payload.nueva_unidad_organizativa = values.nueva_unidad_organizativa;
  }

  if (values.descripcion_unidad_temporis) {
    payload.descripcion_unidad_temporis = values.descripcion_unidad_temporis;
  } else {
    const unitElem = document.querySelector("#nueva_unidad_organizativa .limpiarSelect__single-value");
    if (unitElem?.innerText) {
      payload.descripcion_unidad_temporis = unitElem.innerText;
    }
  }

  if (values.nuevo_plan_horario) {
    payload.nuevo_plan_horario = values.nuevo_plan_horario;
  }
};

const buildTipo3Payload = (values, payload) => {
  if (values.unidad_destino) payload.unidad_destino = values.unidad_destino;
  if (values.instalacion_administrador) {
    const { JEFE, ADMIN } = values.instalacion_administrador;
    if (JEFE) payload.cod_sap_jefe_unidad_destino = JEFE.REEO_CODIGO_SAP;
    if (ADMIN) payload.cod_sap_administrador_destino = ADMIN.REEO_CODIGO_SAP;
    const instElem = document.querySelector("#instalacion_administrador .limpiarSelect__single-value");
    if (instElem) payload.nombre_instalacion_destino = instElem.innerText;
  }
};

const buildTipo4Payload = (values, payload) => {
  if (values.tipo_cambio_contrato) payload.tipo_cambio_contrato = values.tipo_cambio_contrato;
  if (values.fecha_renovacion_contrato) payload.fecha_renovacion_contrato = values.fecha_renovacion_contrato;
};

const buildTipo5Payload = (values, payload) => {
  if (values.motivo_desvinculacion) payload.motivo_desvinculacion = values.motivo_desvinculacion;
  if (values.id_clausula_despido) payload.id_clausula_despido = values.id_clausula_despido;
};

const formatPosicionTexto = (cod, nombre) => {
  if (cod) {
    return `${nombre || ""} (${cod})`.trim();
  }
  return nombre || "N/A";
};

const checkCargoChange = (solicitud, payload) => {
  if (!payload.cod_sap_nuevo_cargo || String(payload.cod_sap_nuevo_cargo) === String(solicitud?.cod_sap_nuevo_cargo)) {
    return null;
  }
  const posAnt = formatPosicionTexto(solicitud?.cod_sap_nuevo_cargo, solicitud?.nombre_nuevo_cargo);
  const posNueva = formatPosicionTexto(payload.cod_sap_nuevo_cargo, payload.nombre_nuevo_cargo);
  return `Se modificó la posición de [${posAnt}] a [${posNueva}]`;
};

const checkFieldChange = (newVal, oldVal, label) => {
  if (newVal && String(newVal) !== String(oldVal || "")) {
    return `${label}: [${oldVal || "N/A"}] -> [${newVal}]`;
  }
  return null;
};

const generateCambiosLog = (solicitud, payload) => {
  const cambiosLog = [
    checkFieldChange(payload.fecha_inicio_cambio, solicitud?.fecha_inicio_cambio, "Fecha efectiva"),
    checkCargoChange(solicitud, payload),
    checkFieldChange(payload.descripcion_unidad_temporis, solicitud?.descripcion_unidad_temporis || solicitud?.nueva_unidad_organizativa, "Unidad Organizativa"),
    checkFieldChange(payload.nuevo_plan_horario, solicitud?.nuevo_plan_horario, "Plan Horario"),
    checkFieldChange(payload.nombre_instalacion_destino, solicitud?.nombre_instalacion_destino, "Instalación Destino"),
    checkFieldChange(payload.tipo_cambio_contrato, solicitud?.tipo_cambio_contrato, "Tipo Cambio Contrato"),
    checkFieldChange(payload.fecha_renovacion_contrato, solicitud?.fecha_renovacion_contrato, "Fecha Renovación Contrato"),
    checkFieldChange(payload.motivo_desvinculacion, solicitud?.motivo_desvinculacion, "Motivo Desvinculación"),
    checkFieldChange(payload.id_clausula_despido, solicitud?.id_clausula_despido, "Cláusula Despido"),
  ].filter(Boolean);

  if (cambiosLog.length > 0) {
    return `Valores modificados por RRHH: ${cambiosLog.join("; ")}`;
  }
  return "Valores modificados por RRHH.";
};

const buildEditarSolicitudPayload = (solicitud, values) => {
  const idTipo = Number(solicitud?.id_tipo_solicitud);
  const payload = {
    id_solicitud: solicitud?.id,
    id_tipo_solicitud: idTipo,
    fecha_inicio_cambio: values.fecha_inicio_cambio,
    observacion_edicion: values.observacion_edicion || "",
  };

  buildCargoPayload(idTipo, values, payload);
  buildUnidadPayload(idTipo, values, payload);

  if (idTipo === 3) buildTipo3Payload(values, payload);
  if (idTipo === 4) buildTipo4Payload(values, payload);
  if (idTipo === 5) buildTipo5Payload(values, payload);

  payload.observacion_edicion = generateCambiosLog(solicitud, payload);
  return payload;
};

const handleEditSuccess = (payload, { setDataListado, dataListado, hideModal, onSuccess }) => {
  mensajeExitoModal({
    titulo: "¡Actualización exitosa!",
    texto: "",
    tiempo: 2200,
  });

  if (setDataListado && dataListado?.arrData) {
    setDataListado((prev) => ({
      ...prev,
      arrData: (prev.arrData || []).map((item) =>
        item.id === payload.id_solicitud
          ? {
              ...item,
              ...payload,
              fecha_inicio_cambio: payload.fecha_inicio_cambio || item.fecha_inicio_cambio,
              nombre_nuevo_cargo: payload.nombre_nuevo_cargo || item.nombre_nuevo_cargo,
              descripcion_unidad_temporis: payload.descripcion_unidad_temporis || item.descripcion_unidad_temporis,
              nuevo_plan_horario: payload.nuevo_plan_horario || item.nuevo_plan_horario,
            }
          : item
      ),
    }));
  }

  if (hideModal) hideModal();
  if (onSuccess) onSuccess();
  window.dispatchEvent(new Event("refresh_solicitudes_table"));
};

  const submitForm = async (values) => {
    const confirmResult = await mensajeConfirmacionModal({
      titulo: "¿Confirmar actualización?",
      texto: `¿Está seguro de guardar las modificaciones a la solicitud #${solicitud?.id}?`,
      confirmButtonText: "Sí, guardar cambios",
      cancelButtonText: "Cancelar",
      mostrarIcono: false,
    });

    if (!confirmResult.isConfirmed) return;

    modalGuardandoCambiosModal("Guardando cambios en la solicitud...");
    try {
      const payload = buildEditarSolicitudPayload(solicitud, values);
      const resp = await putWithResponse("cc/editar_solicitud", null, payload);

      if (resp?.status === 200 && resp?.data?.status === "success") {
        handleEditSuccess(payload, { setDataListado, dataListado, hideModal, onSuccess });
      } else {
        const errorMsg = resp?.data?.message || "No se pudo actualizar la solicitud";
        mensajeEstatico("error", "Error al actualizar", errorMsg);
      }
    } catch (error) {
      console.error("Error editando solicitud:", error);
      mensajeEstatico("error", "Error inesperado", "Ocurrió un problema al procesar la edición.");
    }
  };

  const normalizeTipoCambio = (val) => {
    if (val === null || val === undefined || val === "") return "";
    const s = String(val).trim().toLowerCase();
    if (s === "1" || s === "01" || s.includes("indefinido")) return "01";
    if (s === "2" || s === "02" || s.includes("plazo") || s.includes("extension") || s.includes("extensión") || s.includes("renovac")) return "02";
    return s;
  };

  const formik = useFormik({
    initialValues: {
      fecha_inicio_cambio: solicitud?.fecha_inicio_cambio || "",
      cod_sap_nuevo_cargo: solicitud?.cod_sap_nuevo_cargo || "",
      nombre_nuevo_cargo: solicitud?.nombre_nuevo_cargo || "",
      nueva_unidad_organizativa: solicitud?.nueva_unidad_organizativa || "",
      nuevo_plan_horario: solicitud?.nuevo_plan_horario || "",
      descripcion_unidad_temporis: solicitud?.descripcion_unidad_temporis || "",
      unidad_destino: solicitud?.unidad_destino || "",
      tipo_cambio_contrato: normalizeTipoCambio(solicitud?.tipo_cambio_contrato),
      fecha_renovacion_contrato: solicitud?.fecha_renovacion_contrato || "",
      motivo_desvinculacion: solicitud?.motivo_desvinculacion || "",
      id_clausula_despido: solicitud?.id_clausula_despido || "",
      observacion_edicion: "",
    },
    enableReinitialize: true,
    validate,
    onSubmit: submitForm,
  });

  return {
    formik,
    posicionesLaborales,
    cargando,
  };
};
